import React from "react";
import { IonButton } from "@ionic/react";
import { Calendar } from "@awesome-cordova-plugins/calendar";
import { EventDetail } from "../../models";
import { tsToDate } from "../../utils/ages";
import { useToastStore } from "../../redux/toast";

interface EventAddToCalendarButtonProps {
    event: EventDetail;
    actionsDisabled?: boolean;
    calendarText?: string;
    expand?: 'full' | 'block';
    fill?: 'clear' | 'outline' | 'solid' | 'default';
}

const EventAddToCalendarButton: React.FC<EventAddToCalendarButtonProps> = ({
    event,
    actionsDisabled = false,
    calendarText = "Add to Calendar",
    expand,
    fill,
}) => {
    const toastStore = useToastStore();

    if (!event?.startDateTs) {
        return null;
    }

    const formatLocation = (): string => {
        if (!event.slots || event.slots.length === 0) return "";
        if (event.slots.length === 1 && event.slots[0].facility) {
            const f = event.slots[0].facility;
            return [f.name, f.street1, f.street2, f.suburb, f.city, f.province]
                .filter(Boolean)
                .join(", ");
        }
        return event.slots
            .map((s) => s.facility?.name)
            .filter(Boolean)
            .join(", ");
    };

    const handleCalendarClicked = () => {
        const start = tsToDate(event.startDateTs!);
        const end = event.endDateTs ? tsToDate(event.endDateTs) : undefined;

        const createEvent = () => {
            Calendar.createEventInteractivelyWithOptions(
                event.name,
                formatLocation(),
                event.description || "",
                start,
                end,
                { id: `swool-event-${event.id}-${event.startDateTs}` }
            ).catch((reason) => {
                toastStore.showError({
                    header: "Calendar event could not be created",
                    message: reason,
                });
            });
        };

        Calendar.requestWritePermission()
            .then(() => {
                createEvent();
            })
            .catch(() => {
                toastStore.showError({
                    header: "Calendar is not accessible or permission is denied",
                });
            });
    };

    return (
        <IonButton
            disabled={actionsDisabled}
            color={"tertiary"}
            expand={expand}
            fill={fill}
            onClick={handleCalendarClicked}
        >
            {calendarText}
        </IonButton>
    );
};

export default EventAddToCalendarButton;
